/**
 * Geometry + forecast store (ticket 054).
 *
 * Decoupled from chartStore so the long-running pivots/lines/patterns/trend
 * state/forecast streams don't bloat that store. Receives WS pushes from
 * the geometry dispatcher and exposes per (symbol × interval) reads to
 * overlay components.
 *
 * Active-only lifecycle: when a `pattern`/`trendline` event arrives with
 * `active=false`, the entity is removed from the live set (no historical
 * retention).
 */

import { create } from 'zustand';
import type {
  S2C_SwingPointUpdate,
  S2C_TrendlineUpdate,
  S2C_PatternUpdate,
  S2C_TrendStateUpdate,
  S2C_ForecastUpdate,
} from '../api/types';

type SwingPoint = S2C_SwingPointUpdate['data'];
type Trendline = S2C_TrendlineUpdate['data'];
type Pattern = S2C_PatternUpdate['data'];
type TrendState = S2C_TrendStateUpdate['data'];
type Forecast = S2C_ForecastUpdate['data'];

type Key = string; // `${symbol}:${interval}`

function key(symbol: string, interval: number): Key {
  return `${symbol}:${interval}`;
}

const MAX_PIVOTS_PER_KEY = 200;

export type GeometryState = {
  swingPoints: Record<Key, SwingPoint[]>;
  trendlines: Record<Key, Map<string, Trendline>>;
  patterns: Record<Key, Map<string, Pattern>>;
  trendState: Record<Key, TrendState | undefined>;
  forecastDet: Record<Key, Forecast | undefined>;
  forecastMl: Record<Key, Forecast | undefined>;

  addSwingPoint: (symbol: string, interval: number, pt: SwingPoint) => void;
  upsertTrendline: (symbol: string, interval: number, line: Trendline) => void;
  upsertPattern: (symbol: string, interval: number, pattern: Pattern) => void;
  setTrendState: (symbol: string, interval: number, state: TrendState) => void;
  setForecast: (
    symbol: string,
    interval: number,
    forecast: Forecast,
  ) => void;

  getSwingPoints: (symbol: string, interval: number) => SwingPoint[];
  getActiveTrendlines: (symbol: string, interval: number) => Trendline[];
  getActivePatterns: (symbol: string, interval: number) => Pattern[];
  getTrendState: (symbol: string, interval: number) => TrendState | undefined;
  getForecast: (
    symbol: string,
    interval: number,
    method: 'deterministic' | 'ml',
  ) => Forecast | undefined;
};

export const useGeometryStore = create<GeometryState>((set, get) => ({
  swingPoints: {},
  trendlines: {},
  patterns: {},
  trendState: {},
  forecastDet: {},
  forecastMl: {},

  addSwingPoint: (symbol, interval, pt) => {
    const k = key(symbol, interval);
    set((s) => {
      const list = s.swingPoints[k] ?? [];
      // De-dupe by (pivotTs, kind); keep insertion order.
      if (list.find((p) => p.pivotTs === pt.pivotTs && p.kind === pt.kind)) {
        return s;
      }
      const next = [...list, pt];
      if (next.length > MAX_PIVOTS_PER_KEY) next.splice(0, next.length - MAX_PIVOTS_PER_KEY);
      return { swingPoints: { ...s.swingPoints, [k]: next } };
    });
  },

  upsertTrendline: (symbol, interval, line) => {
    const k = key(symbol, interval);
    set((s) => {
      const map = new Map(s.trendlines[k] ?? []);
      if (!line.active) {
        map.delete(line.lineId);
      } else {
        map.set(line.lineId, line);
      }
      return { trendlines: { ...s.trendlines, [k]: map } };
    });
  },

  upsertPattern: (symbol, interval, pattern) => {
    const k = key(symbol, interval);
    set((s) => {
      const map = new Map(s.patterns[k] ?? []);
      if (!pattern.active) {
        map.delete(pattern.patternId);
      } else {
        map.set(pattern.patternId, pattern);
      }
      return { patterns: { ...s.patterns, [k]: map } };
    });
  },

  setTrendState: (symbol, interval, state) => {
    const k = key(symbol, interval);
    set((s) => ({ trendState: { ...s.trendState, [k]: state } }));
  },

  setForecast: (symbol, interval, forecast) => {
    const k = key(symbol, interval);
    if (forecast.method === 'deterministic') {
      set((s) => ({ forecastDet: { ...s.forecastDet, [k]: forecast } }));
    } else {
      set((s) => ({ forecastMl: { ...s.forecastMl, [k]: forecast } }));
    }
  },

  getSwingPoints: (symbol, interval) => get().swingPoints[key(symbol, interval)] ?? [],
  getActiveTrendlines: (symbol, interval) =>
    Array.from((get().trendlines[key(symbol, interval)] ?? new Map()).values()),
  getActivePatterns: (symbol, interval) =>
    Array.from((get().patterns[key(symbol, interval)] ?? new Map()).values()),
  getTrendState: (symbol, interval) => get().trendState[key(symbol, interval)],
  getForecast: (symbol, interval, method) =>
    method === 'deterministic'
      ? get().forecastDet[key(symbol, interval)]
      : get().forecastMl[key(symbol, interval)],
}));
